import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { scrollToId } from "@/lib/scroll";
import { IMAGES, fmtPrice, displayName } from "@/data/site";
import { ProductImage } from "./ProductImage";
import { useProducts } from "@/lib/products";

export const DanielKlein = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const { products } = useProducts();
  const featured = [...products]
    .filter((p) => p.available)
    .sort((a, b) => {
      const da = a.oldPrice ? 1 - a.price / a.oldPrice : 0;
      const db = b.oldPrice ? 1 - b.price / b.oldPrice : 0;
      return db - da;
    })
    .slice(0, 3);

  return (
    <section
      id="secki"
      ref={ref}
      data-testid="secki-section"
      className="relative overflow-hidden bg-emerald text-ivory py-16 md:py-24 lg:py-32"
    >
      <div className="grain-overlay" />
      <div className="absolute top-0 right-0 pointer-events-none select-none overflow-hidden">
        <p className="font-serif text-[16vw] leading-none font-semibold text-outline-ivory whitespace-nowrap -translate-y-[12%]">
          AS
        </p>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <Reveal className="lg:col-span-5 order-2 lg:order-1">
          <motion.div style={{ y: imgY }} className="relative aspect-[4/5] max-w-[440px]">
            <div className="absolute -inset-3 border border-ivory/20 -translate-x-4 translate-y-4" />
            <div className="relative h-full w-full overflow-hidden bg-emerald-deep">
              <img
                src={IMAGES.danielKlein}
                alt="Atılmaz Saat Seçkisi"
                loading="lazy"
                className="h-full w-full object-cover"
                data-testid="secki-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-emerald-deep/40 via-transparent to-transparent" />
            </div>
          </motion.div>
        </Reveal>

        <div className="lg:col-span-7 order-1 lg:order-2">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-ivory/50">Vitrinden Seçtiklerimiz</p>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.08]">
              Atılmaz Saat<br />
              <span className="italic text-gold">Seçkisi</span>
            </h2>
            <p className="mt-6 max-w-lg text-sm sm:text-base leading-relaxed text-ivory/65 font-light">
              Her ürün, mağazamıza ulaşmadan önce titizlikle seçilir; orijinallik
              belgesi ve garantisiyle, özel kutusunda teslim edilir. Kol saati, takı
              ve gözlükte sezonun en beğenilen tasarımlarını keşfedin.
            </p>
          </Reveal>

          <div className="mt-10 divide-y divide-ivory/10 border-y border-ivory/10">
            {featured.map((p, i) => (
              <Reveal key={p.id} delay={0.1 + i * 0.08}>
                <a
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-4 sm:gap-6 py-4"
                  data-testid={`secki-item-${p.id}`}
                >
                  <span className="font-mono text-xs text-ivory/40 w-7">0{i + 1}</span>
                  <span className="h-16 w-16 sm:h-20 sm:w-20 shrink-0 bg-ivory overflow-hidden">
                    <ProductImage src={p.image} alt={p.name} className="h-full w-full object-contain p-1.5" />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block font-mono text-[9px] uppercase tracking-[0.25em] text-ivory/40">{p.brand}</span>
                    <span className="block font-serif text-lg sm:text-xl font-medium truncate transition-colors group-hover:text-gold-bright">{displayName(p.name)}</span>
                    <span className="mt-0.5 block text-sm text-ivory/50">
                      <span className="text-ivory font-semibold text-base">{fmtPrice(p.price)}</span>
                      {p.oldPrice && <span className="ml-2 line-through">{fmtPrice(p.oldPrice)}</span>}
                    </span>
                  </span>
                  <span
                    data-testid={`secki-cta-${p.id}`}
                    className="shrink-0 inline-flex items-center gap-2 text-[13px] font-semibold text-ivory/80 group-hover:text-ivory transition-colors"
                  >
                    Satın Al <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </a>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.3}>
            <button
              onClick={() => scrollToId("#katalog")}
              data-testid="secki-cta"
              className="mt-10 inline-flex items-center gap-3 rounded-full border border-ivory/30 px-8 h-12 text-sm font-semibold text-ivory transition-colors duration-300 hover:bg-ivory hover:text-emerald"
            >
              Tüm Ürünleri Gör <ArrowRight size={15} />
            </button>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
